import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Link,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LaunchIcon from '@mui/icons-material/Launch';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Custom renderer for links to handle [ref:...] markers
const MarkdownComponents = (onInsightLinkClick) => ({
  // Intercept link rendering to handle [ref:ID] pattern
  a: ({ node, children, href, ...props }) => {
    // Check if this is a [ref:ID] link
    const refMatch = href?.match(/^ref:([a-zA-Z0-9]+)$/);
    
    if (refMatch) {
      const insightId = refMatch[1];
      return (
        <Tooltip title="View Source Thread">
          <Link
            component="button"
            variant="body1"
            onClick={(e) => {
              e.preventDefault();
              onInsightLinkClick(insightId);
            }}
            sx={{ 
              verticalAlign: 'baseline', 
              mx: 0.5, 
              p: 0, 
              minWidth: 0, 
              height: 'auto', 
              lineHeight: 1,
              cursor: 'pointer'
            }}
          >
            <LaunchIcon sx={{ fontSize: '1rem' }} />
          </Link>
        </Tooltip>
      );
    }
    
    // Regular external link
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  }
});

const AmsReportModal = ({ 
  open, 
  onClose, 
  reportData, 
  isLoading, 
  onExport, 
  onShare, 
  onAsana, 
  isAsanaAvailable, 
  onInsightLinkClick 
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Chat Summary
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Generating your report...</Typography>
          </Box>
        ) : reportData ? (
          <>
            <Typography variant="h6" gutterBottom>
              {reportData.metadata?.clientName}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Report for period: {reportData.metadata?.dateRange?.from} to {reportData.metadata?.dateRange?.to}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  // Header styling
                  '& h1': { 
                    fontSize: '1.75rem', 
                    fontWeight: 700, 
                    mt: 3, 
                    mb: 2,
                    color: '#1a1a1a',
                    borderBottom: '2px solid #e0e0e0',
                    pb: 1
                  },
                  '& h2': { 
                    fontSize: '1.4rem', 
                    fontWeight: 600, 
                    mt: 3, 
                    mb: 1.5,
                    color: '#2a2a2a'
                  },
                  '& h3': { 
                    fontSize: '1.15rem', 
                    fontWeight: 600, 
                    mt: 2, 
                    mb: 1,
                    color: '#333'
                  },
                  // Horizontal rule
                  '& hr': { 
                    my: 3, 
                    border: 'none', 
                    borderTop: '2px solid #e0e0e0' 
                  },
                  // Paragraph spacing
                  '& p': { 
                    mb: 1.5 
                  },
                  // Bold text
                  '& strong': { 
                    fontWeight: 600,
                    color: '#1a1a1a'
                  },
                  // Lists
                  '& ul, & ol': { 
                    pl: 3, 
                    mb: 2 
                  },
                  '& li': { 
                    mb: 0.5 
                  },
                  // Tables
                  '& table': { 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    mb: 3,
                    mt: 2
                  },
                  '& th': { 
                    backgroundColor: '#f0f0f0',
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 600,
                    borderBottom: '2px solid #ddd'
                  },
                  '& td': { 
                    padding: '10px 12px',
                    borderBottom: '1px solid #e0e0e0'
                  },
                  '& tr:hover': {
                    backgroundColor: '#f9f9f9'
                  },
                  // Code blocks
                  '& code': {
                    backgroundColor: '#f0f0f0',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '0.9em',
                    fontFamily: 'monospace'
                  }
                }}
              >
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={MarkdownComponents(onInsightLinkClick)}
                >
                  {reportData.reportText}
                </ReactMarkdown>
              </Box>
            </Box>
          </>
        ) : (
          <Typography>No report data available.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose}>Close</Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onShare}
            disabled={isLoading || !reportData}
          >
            Share via Email
          </Button>
          <Button
            variant="outlined"
            onClick={() => onExport('word')}
            disabled={isLoading || !reportData}
          >
            Export to Word
          </Button>
          <Tooltip title={isAsanaAvailable ? "Create Task in Asana" : "Connect Asana in Account tab"}>
            <span>
              <Button
                variant="contained"
                onClick={onAsana}
                disabled={isLoading || !reportData || !isAsanaAvailable}
                startIcon={<PlaylistAddCheckIcon />}
              >
                Create Task
              </Button>
            </span>
          </Tooltip>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AmsReportModal;